
import Component from "vue-class-component";
import { Action} from "vuex-class";
import { TenantSystemPrefs } from "@/store/types";
import {
    AreaEventHistoryColumnKey,
    AreaEventHistoryColumnPref
} from "@/types/sv-data/AreaEventHistory";
import { TableHeader } from "@/components/table/generic-table.vue";
import AreaEventHistoryColumnsMixin from "@/mixins/AreaEventHistoryColumnsMixin";
import { buildAvailableColumns, parseEventAreaHistoryColumnPrefs } from "@/utils/areaEventHistoryUtils";
import { Mixins } from "vue-property-decorator";

@Component({})
export default class AreaEventHistorySettingsForm extends Mixins(AreaEventHistoryColumnsMixin) {
    @Action createOrUpdateAreaEventHistorySettings: (updatedPrefs: TenantSystemPrefs) => Promise<void>;

    // Existing (saved) column visibility
    private savedColumnVisibility: Record<string, boolean> = {};
    // New (interactable) column visibility
    private newColumnVisibility: Record<string, boolean> = {};
    // New autohandled default value
    private newAutohandledDefault: boolean = false;
    private savedAutohandledDefault: boolean = false;

    private get availableColumns(): TableHeader[] {
        return buildAvailableColumns(this.getFeature);
    }

    private get configureDefaultColumns(): boolean {
        return this.getFeature(["Areas", "AreaEventHistory", "ColumnConfiguration", "ColumnVisibility"]);
    }

    private get configureDefaultAutohandled(): boolean {
        return this.getFeature(["Areas", "AreaEventHistory", "ColumnConfiguration", "DefaultAutohandled"]);
    }

    private buildColumnPrefs(): AreaEventHistoryColumnPref[] {
        return this.availableColumns.map(column => ({
            key: column.key as AreaEventHistoryColumnKey,
            visible: this.newColumnVisibility[column.key] !== undefined
                ? this.newColumnVisibility[column.key]
                : !!column.visible
        }));
    }

    private beforeMount(): void {
        if (this.configureDefaultColumns) {
            let parsedPrefs: AreaEventHistoryColumnPref[] = [];

            try {
                const prefValue = this.tenantSystemPrefs?.areaEventHistorySettings?.columnConfig ?? null;
                parsedPrefs = prefValue ? parseEventAreaHistoryColumnPrefs(prefValue) : [];
            } catch (error) {
                console.error("Failed to parse Area Event History column preferences:", error);
            }

            const prefMap = new Map(parsedPrefs.map(p => [p.key, p]));
            const columnVisibility: Record<string, boolean> = {};
            for (const column of this.availableColumns) {
                const pref = prefMap.get(column.key as AreaEventHistoryColumnKey) as AreaEventHistoryColumnPref | undefined;
                columnVisibility[column.key] = pref !== undefined ? pref.visible : !!column.visible;
            }

            this.newColumnVisibility = { ...columnVisibility };
            this.savedColumnVisibility = { ...columnVisibility };
        }

        if (this.configureDefaultAutohandled) {
            const autohandled = this.tenantSystemPrefs?.areaEventHistorySettings?.defaultAutohandled ?? false;
            this.newAutohandledDefault = autohandled;
            this.savedAutohandledDefault = autohandled;
        }
    }

    private get columnVisibilitySettingsChanged(): boolean {
        for (const column of this.availableColumns) {
            if (this.newColumnVisibility[column.key] !== this.savedColumnVisibility[column.key]) {
                return true;
            }
        }
        return false;
    }

    private get autohandledDefaultChanged(): boolean {
        return this.newAutohandledDefault !== this.savedAutohandledDefault;
    }

    private get haveSettingsChanged(): boolean {
        return this.columnVisibilitySettingsChanged || this.autohandledDefaultChanged;
    }

    private resetChanges(): void {
        if (this.configureDefaultColumns) {
            this.newColumnVisibility = { ...this.savedColumnVisibility };
        }
        if (this.configureDefaultAutohandled) {
            this.newAutohandledDefault = this.savedAutohandledDefault;
        }
    }

    private async saveChanges(): Promise<void> {
        const updatedPrefs: TenantSystemPrefs = {
            ...this.tenantSystemPrefs,
            areaEventHistorySettings: {
                ...this.tenantSystemPrefs?.areaEventHistorySettings,
                columnConfig: JSON.stringify(this.buildColumnPrefs()),
                defaultAutohandled: this.newAutohandledDefault
            }
        };

        try {
            await this.createOrUpdateAreaEventHistorySettings(updatedPrefs);
        } catch (err) {
            console.error("Failed to save Area Event History settings:", err);
            this.$notify({
                type: "error",
                title: "Failed to save Area Event History settings. Please try again."
            });
            return;
        }

        if (this.configureDefaultColumns) {
            this.savedColumnVisibility = { ...this.newColumnVisibility };
        }
        if (this.configureDefaultAutohandled) {
            this.savedAutohandledDefault = this.newAutohandledDefault;
        }

        this.$notify({
            type: "success",
            title: "Area Event History settings updated"
        });
    }

    private get isNoColumnsSelected(): boolean {
        if (!this.configureDefaultColumns) {
            // If column configuration is not enabled, we don't consider it an error to have no columns selected.
            return false;
        }

        return this.availableColumns.every(column => !this.newColumnVisibility[column.key]);
    }

    private get isSaveDisabled(): boolean {
        return !this.haveSettingsChanged || this.isNoColumnsSelected;
    }
}
